import { motion } from "motion/react"
import { ArrowRight, Check } from "@untitledui/icons"
import type { ActivationView } from "@/lib/api/onboarding-activation"
import { SubscriptionDetails } from "./activation-chrome"
import { PrimaryButton, ViewfinderTarget, sectionLabelClass } from "./ui"

// The cover. One job: say exactly what this is and what to do, in plain
// words, then one button. Addressed to the shop — never to a named person.
export function WelcomeStep({
  data,
  onStart,
}: {
  data: ActivationView
  onStart: () => void
}) {
  const repFirst = data.salesRep.name.split(" ")[0]

  const reassurances = [
    "Takes about 3 minutes",
    "Nothing is charged today",
    "No account or password needed",
  ]

  const handy = [
    "Your shop's address, phone, and email",
    "The estimating system you write estimates in",
    "Your IT contact's email and phone",
    "The email where invoices should go",
  ]

  const steps = [
    { n: 1, label: "Your shop's details" },
    { n: 2, label: "The systems you use" },
    { n: 3, label: "Your IT & billing contacts" },
    { n: 4, label: "Read and sign the data authorization" },
    { n: 5, label: "Choose your installation call time" },
  ]

  return (
    <div>
      {/* the viewfinder acquires the shop — the logo's brackets, working */}
      <div className="-ml-3 mb-2">
        <ViewfinderTarget>
          <span className="whitespace-nowrap text-[13px] font-bold tracking-wide text-emerald-700">
            {data.shop.name}
          </span>
        </ViewfinderTarget>
      </div>
      <h1 className="text-[26px] font-bold leading-[1.15] tracking-tight text-[#0C2018] md:text-[30px]">
        Let's set up your shop.
      </h1>
      <p className="mt-2.5 max-w-lg text-[15px] leading-relaxed text-[#5A6A61]">
        Fill in five short steps to connect your shop and book your installation call. If you
        have a question at any point, call {repFirst}
        {data.salesRep.phone ? ` at ${data.salesRep.phone}` : ""}.
      </p>

      {/* reassurances — three quiet cells */}
      <div className="mt-7 grid gap-px overflow-hidden rounded-xl border border-[#E8ECEA] bg-[#E8ECEA] sm:grid-cols-3">
        {reassurances.map((r, i) => (
          <motion.div
            key={r}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 + i * 0.07 }}
            className="flex items-center gap-2.5 bg-white px-4 py-3.5"
          >
            <span className="grid size-5 shrink-0 place-items-center rounded-full bg-emerald-50">
              <Check className="size-3 text-emerald-600" strokeWidth={3} aria-hidden />
            </span>
            <span className="text-[13px] font-medium leading-snug text-[#33413A]">{r}</span>
          </motion.div>
        ))}
      </div>

      {/* the five steps — mobile only; the sidebar already lists them on desktop */}
      <div className="mt-7 lg:hidden">
        <p className={`mb-2 ${sectionLabelClass}`}>The five steps</p>
        <ol className="overflow-hidden rounded-lg border border-[#E8ECEA]">
          {steps.map((s, i) => (
            <motion.li
              key={s.n}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + i * 0.05 }}
              className="flex items-center gap-3 border-b border-[#EDF1EE] bg-white px-4 py-2.5 last:border-b-0"
            >
              <span className="grid size-6 shrink-0 place-items-center rounded-full border border-[#D5DDD8] text-[11px] font-semibold text-[#718077]">
                {s.n}
              </span>
              <span className="min-w-0 flex-1 text-[14px] font-medium text-[#33413A]">{s.label}</span>
            </motion.li>
          ))}
        </ol>
      </div>

      {/* what to have nearby — fewer mid-flow stalls */}
      <div className="mt-7">
        <p className={`mb-2.5 ${sectionLabelClass}`}>Good to have handy</p>
        <ul className="space-y-2">
          {handy.map((h, i) => (
            <motion.li
              key={h}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.24 + i * 0.06 }}
              className="flex items-center gap-3 text-[14px] text-[#4A5A51]"
            >
              <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-emerald-500" />
              {h}
            </motion.li>
          ))}
        </ul>
      </div>

      {/* the subscription — fully visible, nothing to click */}
      <div className="mt-7">
        <p className={`mb-2.5 ${sectionLabelClass}`}>Your subscription</p>
        <SubscriptionDetails data={data} />
      </div>

      <div className="mt-8 flex items-center justify-end border-t border-[#EDF1EE] pt-5">
        <PrimaryButton onClick={onStart}>
          Start step 1
          <ArrowRight className="size-4" />
        </PrimaryButton>
      </div>
    </div>
  )
}
